import createArticleCard from './util/create-article-card';

const wrapper = document.querySelector('#article-listing');
const listing = wrapper?.querySelector('.post-listing');

if (wrapper && listing) {
  // The listing pages can support filtering by multiple tags, but
  // it can often lead to no results when multiple tags are selected.
  // By default we disable this feature, but it can be enabled by
  // setting a global variable on the window object
  const supportMultipleTags = () => window.supportMultipleTags ?? false;

  const endpoint = '/apis/micrositeapi/getarticlecards';
  const filterWrapper = wrapper.querySelector('.article-tag-filters');
  const allTagsButton = filterWrapper?.querySelector('.all-tags');
  const tagButtons = Array.from(
    filterWrapper?.querySelectorAll('.tag:not(.all-tags)') ?? []
  );

  const loadMoreWrapper = wrapper.querySelector('#listing-load-more');
  const loadMoreButton = loadMoreWrapper.querySelector('.load-more');
  const noPostsMessage = wrapper.querySelector('#listing-no-posts');
  const errorMessage = wrapper.querySelector('#listing-error');

  const rootId = wrapper.dataset.root;
  const categoryId = wrapper.dataset.category || 0;

  let currentPage = 0;
  let isLoading = false;
  let canLoadMore = !loadMoreWrapper.classList.contains('hidden');
  const loadMoreText = loadMoreButton.innerText;

  const toggleButton = (button, isActive) => {
    if (!button) return;
    button.classList.toggle('active', isActive);
    button.setAttribute('aria-pressed', isActive);
  };

  const getSelectedTags = () => {
    return tagButtons
      .filter(b => b.classList.contains('active'))
      .map(b => b.dataset.tag)
      .filter(id => id && id !== categoryId);
  };

  const getCurrentFiltersKey = () => {
    return `${categoryId}-${currentPage}-${getSelectedTags().join('-')}`;
  };

  let currentFiltersKey = getCurrentFiltersKey();
  const cachedData = {};

  const setPostsFocusable = canFocus => {
    listing.querySelectorAll('a').forEach(link => {
      if (canFocus) {
        link.removeAttribute('tabindex');
      } else {
        link.tabIndex = -1;
      }
    });

    if (document.activeElement?.closest('.post-listing')) {
      document.activeElement.blur();
      listing.tabIndex = -1;
      setTimeout(() => listing.focus());
    }
  };

  const fetchPosts = async isLoadMore => {
    currentPage = isLoadMore ? currentPage + 1 : 0;

    // If a subsequent request finishes before this one,
    // we dont update the UI with the stale data
    const filtersKey = getCurrentFiltersKey();
    currentFiltersKey = filtersKey;

    const tags = getSelectedTags();

    if (cachedData[filtersKey]) {
      refreshPostListing(cachedData[filtersKey], tags, isLoadMore);
      return;
    }

    isLoading = true;

    const url = new URL(endpoint, window.location.origin);
    const minLoadingUiDelay = window.minLoadingUiDelay ?? 100;
    const minLoadingUiDuration = window.minLoadingUiDuration ?? 350;

    url.searchParams.set('root', rootId);
    url.searchParams.set('category', categoryId);
    url.searchParams.set('page', currentPage);
    if (tags.length) url.searchParams.set('tags', tags.join('-'));

    // Wait for a minimum delay before showing the loading UI, and
    // keep it visibile for a minimum duration to avoid flickering
    // let canShowNewPosts = true;
    let loadingUiPromise = Promise.resolve();

    loadMoreButton.disabled = true;
    if (isLoadMore) loadMoreButton.innerText = 'Loading articles…';

    const loadingUiTimeout = setTimeout(() => {
      if (!isLoadMore && filtersKey === currentFiltersKey) {
        listing.classList.add('loading');
        setPostsFocusable(false);
      }

      loadingUiPromise = new Promise(resolve => {
        setTimeout(resolve, minLoadingUiDuration);
      });
    }, minLoadingUiDelay);

    try {
      const res = await fetch(url);
      const data = (await res.json()) ?? {};

      if ('posts' in data && 'areMorePostsAvailable' in data) {
        cachedData[filtersKey] = data;
      }

      clearTimeout(loadingUiTimeout);
      await loadingUiPromise;

      if (filtersKey !== currentFiltersKey) return;

      if (typeof data !== 'object' || !Array.isArray(data.posts)) {
        throw new Error('Invalid response');
      }

      refreshPostListing(data, tags, isLoadMore);
    } catch (error) {
      console.error('Failed to fetch posts', error);

      if (filtersKey !== currentFiltersKey) return;

      isLoading = false;
      canLoadMore = false;
      loadMoreButton.disabled = false;
      loadMoreButton.innerText = loadMoreText;
      listing.classList.remove('loading');
      errorMessage.classList.remove('hidden');
      listing.classList.add('hidden');
      noPostsMessage.classList.add('hidden');
      loadMoreWrapper.classList.add('hidden');
      setPostsFocusable(true);
    }
  };

  const refreshPostListing = (data, tags, isLoadMore) => {
    const { posts, areMorePostsAvailable } = data;

    isLoading = false;
    loadMoreButton.disabled = false;
    loadMoreButton.innerText = loadMoreText;
    listing.classList.remove('loading');
    errorMessage.classList.add('hidden');
    canLoadMore = posts.length && areMorePostsAvailable;
    loadMoreWrapper.classList.toggle('hidden', !canLoadMore);

    if (!posts.length) {
      if (!isLoadMore) listing.innerText = '';

      noPostsMessage
        .querySelector('.has-tags')
        ?.classList.toggle('hidden', !!tags.length);
      noPostsMessage
        .querySelector('.no-tags')
        ?.classList.toggle('hidden', !tags.length);
      noPostsMessage.classList.remove('hidden');

      return;
    }

    listing.classList.remove('hidden');
    noPostsMessage.classList.add('hidden');

    const fragment = document.createDocumentFragment();

    posts.forEach(post => {
      const card = createArticleCard(post);
      if (card) fragment.append(card);
    });

    if (!isLoadMore) listing.innerText = '';

    listing.append(fragment);

    setPostsFocusable(true);
  };

  // Select individual tag
  tagButtons.forEach(button => {
    button.addEventListener('click', () => {
      const isActive = button.classList.contains('active');

      if (isActive) {
        if (!supportMultipleTags()) return;

        toggleButton(button, false);

        if (!tagButtons.some(b => b.classList.contains('active'))) {
          toggleButton(allTagsButton, true);
        }
      } else {
        if (!supportMultipleTags()) {
          tagButtons.forEach(b => toggleButton(b, false));
        }

        toggleButton(button, true);
        toggleButton(allTagsButton, false);
      }

      fetchPosts();
    });
  });

  // Select all tags
  allTagsButton?.addEventListener('click', () => {
    const isActive = allTagsButton.classList.contains('active');

    if (isActive) {
      if (!getSelectedTags().length) return;
      toggleButton(allTagsButton, false);
    } else {
      toggleButton(allTagsButton, true);
      tagButtons.forEach(b => toggleButton(b, false));
    }

    fetchPosts();
  });

  // Show more tags
  wrapper.querySelectorAll('.show-more-tags').forEach(showMoreTagsButton => {
    showMoreTagsButton.addEventListener('click', () => {
      showMoreTagsButton
        .closest('.article-tags')
        ?.querySelectorAll('li')
        .forEach(li => li.classList.remove('hidden'));

      showMoreTagsButton.closest('li')?.classList.add('hidden');
    });
  });

  // Load more posts
  loadMoreButton.addEventListener('click', () => {
    if (!canLoadMore || isLoading) return;
    fetchPosts(true);
  });

  // Disable clicking on articles while loading
  listing.addEventListener(
    'click',
    e => {
      if (isLoading) e.preventDefault();
    },
    { capture: true }
  );
}
